import { useState, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { withStyles } from '@mui/styles';
import {
  Form,
  TextField,
  Select,
  Checkbox,
} from '@calefy-inc/informedMaterial';
import { connect } from 'react-redux';
import { formModifierStore } from '../../../../../../../../../store';

import ComponentRegistry from '../../../../../../../../common/QuoteComponents';
import { addNodeUnderParent } from 'react-sortable-tree';
import getTopLevelQuestion from '../../../FormModifierBuilder/common/getTopLevelQuestion';
import {
  validateExists,
  filterNotNullishOrEmpty,
  questionInstanceFormDirty,
  validateApiName,
  generateValidateLanguages,
} from './utility';
import { LanguageFields } from './LanguageFields';
import { DisplayFormErrors } from './DisplayFormErrors';

// types and classes
import { FormApi, FormState } from 'informed';
import type { $TSFixMe, GenericObject } from '@calefy-inc/utilityTypes';
import { Language } from '../../../../../../../../../Typescript/classes';
import { ProgramBuilderQuestionInstance } from '../../../../../../../../FormManager/classes';
import { StoreState, Dispatch } from '../../../../../../../../../store';
import { FinalFormModifier } from '../../../../classes';
import { HideInLocations } from '../../../../../../../../FormManager/components/CreateQuestionInstance/QuestionInstanceForm/HideInLocations';
import type { QuestionInstanceFormError } from './types';
import { useAllowByPermission } from '../../../../../../../../../hooks';

const quoteComponentTypes = ComponentRegistry.getComponentTypes().map(
  ({ type, typeLabel }) => ({ value: type, label: typeLabel }),
);

interface QuestionInstanceFormDisplayProps
  extends ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
  handleClose: $TSFixMe;
  onCustomSave: $TSFixMe;
  questionInstance?: ProgramBuilderQuestionInstance;
  classes: GenericObject;
  parentForm: $TSFixMe;
  forms: $TSFixMe;
  isEditing: boolean;
  path: $TSFixMe;
  node: $TSFixMe;
  clearPendingQuestion: () => void;
  setDirty: $TSFixMe;
  updateEditAll: $TSFixMe;
  languages: Language[];
  [k: string]: $TSFixMe;
}

/**
 * Displays a form to enter information about a given QuestionInstance
 * @param {function} setDirty - used to set information about whether the form is dirty or not. Used in the FormModal to decide whether to display a confirmation dialog before a user leaves a form that they have entered information into.
 */
export function QuestionInstanceFormDisplay({
  handleClose,
  /* searchAndDestroy, */
  onQuestionInstanceCreated,
  onQuestionInstanceEdited,
  onCustomSave,
  questionInstance,
  classes = {},
  parentForm,
  forms,
  isEditing,
  path,
  node,
  clearPendingQuestion,
  setDirty,
  updateEditAll,
  languages,
  // @ts-expect-error
  ...otherProps
}: QuestionInstanceFormDisplayProps) {
  const { questionInstances } = parentForm;
  const hasUpdateApiNamesPermission = useAllowByPermission('update:api:names');
  const isModifiedExisting =
    !!questionInstance && questionInstance.modified === true;
  const isNewQuestion = !questionInstance?.id;
  const canEditApiName =
    !isModifiedExisting &&
    (hasUpdateApiNamesPermission === true || isNewQuestion);
  // @ts-expect-error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingFormState, setPendingFormState] = useState<$TSFixMe>({});
  const [topLevelQuestion, setTopLevelQuestion] =
    useState<ProgramBuilderQuestionInstance | null>(null);
  // @ts-expect-error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editAllSiblingFormCount, setEditAllSiblingsFormCount] =
    useState<number>(0);
  // yes, this is formState.values
  const [currentFormValues, setCurrentFormValues] = useState<$TSFixMe>({});
  const [updatedParentForm, setUpdatedParentForm] =
    useState<$TSFixMe>(parentForm);
  const [newQuestionInstance, setNewQuestionInstance] =
    useState<ProgramBuilderQuestionInstance | null>(questionInstance || null);
  const [unsavedChangesPresent, setUnsavedChangesPresent] =
    useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<$TSFixMe>();
  const formApiRef = useRef<FormApi>();

  const [formError, setFormError] = useState<
    QuestionInstanceFormError | undefined
  >(undefined);

  // set the top-level question from the passed-in questionInstance
  useEffect(() => {
    let top_level_question = null;
    if (questionInstance) {
      top_level_question = getTopLevelQuestion(
        questionInstance,
        questionInstances,
      );
    } else {
      if (node) {
        top_level_question = getTopLevelQuestion(node, questionInstances);
      }
    }
    setTopLevelQuestion(top_level_question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // set the editAllSiblingFormCount when we get the top-level questionInstance
  useEffect(() => {
    setEditAllSiblingsFormCount(
      topLevelQuestion && topLevelQuestion.ancillary.parentForms
        ? topLevelQuestion.ancillary.parentForms.filter(
            (myForm) => parentForm.id !== myForm.id,
          ).length
        : 0,
    );
  }, [topLevelQuestion, parentForm]);

  // update the newQuestionInstance and parentForm as appropriate
  useEffect(() => {
    // first check if the form is empty - if so, clean the question
    if (
      Object.values(currentFormValues).filter(filterNotNullishOrEmpty)
        .length === 0
    ) {
      setNewQuestionInstance(questionInstance ? questionInstance : null);
      return;
    }

    const dataType =
      ComponentRegistry.getComponentDataType(
        currentFormValues.component as string,
      ) || 'string';

    // if the questionInstance already exists, then we want to grab some (but not all) of the properties to map onto the new question instance

    let generatedQuestionInstance =
      ProgramBuilderQuestionInstance.generateFromValues({
        formValues: currentFormValues,
        languages,
        oldQuestion: questionInstance,
        otherAttributes: {
          dataType,
        },
      });

    const gatherSubQuestions = ComponentRegistry.getGatherSubQuestionsFunction(
      currentFormValues.component as string,
    );

    let newUpdatedParentForm = {};
    if (gatherSubQuestions) {
      generatedQuestionInstance = gatherSubQuestions(
        currentFormValues,
        generatedQuestionInstance,
      );
      newUpdatedParentForm = parentForm;
    }

    setNewQuestionInstance(generatedQuestionInstance);
    setUpdatedParentForm(newUpdatedParentForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    questionInstance,
    currentFormValues,
    isEditing,
    parentForm,
    topLevelQuestion,
  ]);

  // Whenever the old and new questionInstances change, update whether there are unsaved changes
  useEffect(() => {
    const changesPresent = questionInstanceFormDirty({
      initialFormValues,
      currentFormValues,
      oldQuestion: questionInstance,
      newQuestion: newQuestionInstance,
    });
    setUnsavedChangesPresent(changesPresent);
  }, [
    questionInstance,
    newQuestionInstance,
    setUnsavedChangesPresent,
    currentFormValues,
    initialFormValues,
  ]);

  // when there are unsaved changes, fire off the setDirty function
  useEffect(() => {
    setDirty(unsavedChangesPresent);
  }, [unsavedChangesPresent, setDirty]);

  // set the initial form values based on the passed-in question instance
  useEffect(() => {
    if (questionInstance) {
      setInitialFormValues(questionInstance.convertToFormState());
    } else {
      setInitialFormValues(null);
    }
  }, [questionInstance]);

  if (isEditing && !questionInstance) {
    handleClose();
    return null;
  }

  const handleSubmit = (
    // @ts-expect-error
    formState: $TSFixMe,
    questionInstance: ProgramBuilderQuestionInstance | null,
    topLevelQuestion: ProgramBuilderQuestionInstance | null,
  ) => {
    if (isEditing) {
      onQuestionInstanceEdited({
        parentForm: updatedParentForm,
        node,
        path,
        questionInstance,
        topLevelQuestion,
      });
    } else {
      onQuestionInstanceCreated({
        parentForm: updatedParentForm,
        forms,
        node,
        path,
        // @ts-expect-error
        questionInstance,
        // @ts-expect-error
        topLevelQuestion,
      });
    }
    clearPendingQuestion();
    handleClose();
  };

  if (initialFormValues === undefined) {
    return null;
  }
  return (
    <>
      <Form
        className={classes.form}
        autoComplete='off'
        noValidate
        allowEmptyStrings={true}
        initialValues={initialFormValues}
        data-testid='QuestionInstanceForm'
        formApiRef={formApiRef}
        onChange={(formState: FormState) => {
          setCurrentFormValues(formState.values ? { ...formState.values } : {});
        }}
        onSubmit={(formState) => {
          const validateLanguages = generateValidateLanguages(languages);
          const error = validateLanguages(formState.values);
          setFormError(error);

          if (!error) {
            const { values } = formState;
            handleSubmit(values, newQuestionInstance, topLevelQuestion);
          }
        }}
      >
        {({ formState }: { formState: FormState }) => {
          let AdditionalCustomizationOptions;
          if (formState.values.component) {
            AdditionalCustomizationOptions =
              ComponentRegistry.getProgramBuilderComponent(
                formState.values.component as string,
              );
          }
          return (
            <>
              <Select
                name='component'
                label='Question Input Type'
                inputId='component-select'
                isClearable={true}
                validate={validateExists}
                required
                className={classes.selectField}
                classes={classes}
                options={quoteComponentTypes}
                placeholder='Select the type of UI input displayed to the user'
                containerTestId='component-selection'
              />
              {languages.map((language) => {
                return (
                  <LanguageFields
                    setApiNameWhenNameSet={canEditApiName}
                    languages={languages}
                    language={language}
                    classes={classes}
                    formError={formError}
                  />
                );
              })}
              <Tooltip
                title={
                  isModifiedExisting
                    ? 'As this is a modification to an existing question, the API name cannot be changed'
                    : canEditApiName !== true
                    ? 'You do not have permission to update the API name of a saved question'
                    : ''
                }
              >
                <span>
                  <TextField
                    className={classes.textField}
                    classes={classes}
                    label='Enter the private name of the question. This will be used internally to retrieve client answer data. It will not be shown to the user.'
                    name='apiName'
                    disabled={canEditApiName !== true}
                    required
                    validate={validateApiName}
                    id='name'
                    variant='standard'
                  />
                </span>
              </Tooltip>
              {!!questionInstance && AdditionalCustomizationOptions ? (
                <AdditionalCustomizationOptions
                  parentForm={parentForm}
                  questionInstances={questionInstances}
                  classes={classes}
                  languages={languages}
                  questionInstance={questionInstance}
                />
              ) : null}
              <Checkbox
                name='required'
                label='Required'
                helperText='Checking this box will make this question instance required'
                classes={classes}
                className={classes.checkbox}
              />

              {!topLevelQuestion ||
              (questionInstance &&
                topLevelQuestion.equals(questionInstance)) ? (
                <Checkbox
                  name='askOnRenewal'
                  label='Ask on Renewal'
                  helperText='Checking this box will make this a question asked on the renewal form'
                  classes={classes}
                  className={classes.checkbox}
                />
              ) : null}
              {formState.values.askOnRenewal === true ? (
                <Checkbox
                  name='prefillOnRenewal'
                  label='Prefill answers on renewal'
                  helperText='Checking this prefills this question on a renewal form with the previous answer'
                  classes={classes}
                  className={classes.checkbox}
                />
              ) : null}
              <HideInLocations />
              <DisplayFormErrors formError={formError} />
              <div className={classes.buttonContainer}>
                <Button onClick={() => handleClose()}>Cancel</Button>
                <Button variant='contained' type='submit'>
                  Submit
                </Button>
              </div>
            </>
          );
        }}
      </Form>
    </>
  );
}

/* Theming */
const QuestionInstanceFormWithStyles = withStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  selectField: {
    margin: `${theme.spacing(2)} 0`,
    '& > label': {
      ...theme.typography.label,
      marginBottom: theme.spacing(1.5),
    },
  },
  textField: {
    margin: `${theme.spacing(2)} 0`,
  },
  checkbox: {
    // marginTop: theme.spacing(1.5),
    '& > label': {
      ...theme.typography.label,
    },
  },
  sliderFormGroup: {
    display: 'flex',
    flexDirection: 'column',
    '& $textField': {
      marginTop: theme.spacing(1.5),
    },
  },
  textarea: {
    maxWidth: '100%',
    margin: `${theme.spacing(3)} 0`,
  },
  calendar: {
    display: 'flex',
    maxWidth: '100%',
    margin: `${theme.spacing(2)} 0`,
  },
  instructionText: {
    ...theme.typography.caption,
    marginLeft: theme.spacing(6),
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    alignSelf: 'flex-end',
    '& > button': {
      marginLeft: theme.spacing(1.5),
    },
  },
  formErrorText: {
    color: theme.palette.error.main,
  },
  finalLanguageDivider: {
    marginBottom: theme.spacing(2),
  },
}))(QuestionInstanceFormDisplay);

/* Redux connect */

function mapStateToProps(state: StoreState) {
  const { currentlyEditingFinalFormModifier, pendingQuestion, languages } =
    state.formModifierStore;

  let path, node;
  if (pendingQuestion) {
    path = pendingQuestion.path;
    node = pendingQuestion.node;
  }
  return {
    currentlyEditingFinalFormModifier,
    path,
    node,
    languages,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  function onQuestionInstanceEdited({
    node,
    path,
    questionInstance,
    topLevelQuestion,
  }: $TSFixMe) {
    dispatch(
      formModifierStore.actions.updateQuestionInstance({
        node,
        path,
        questionInstance,
        topLevelQuestion,
      }),
    );
  }

  function onQuestionInstanceCreated({
    parentForm,
    path,
    questionInstance,
    topLevelQuestion,
  }: {
    parentForm: FinalFormModifier;
    node: $TSFixMe;
    path: $TSFixMe;
    questionInstance: ProgramBuilderQuestionInstance;
    topLevelQuestion: ProgramBuilderQuestionInstance;
  }) {
    const recursiveTransform = (list: Array<$TSFixMe>): $TSFixMe =>
      list.map((ele) => {
        if (
          topLevelQuestion &&
          (ele.id === topLevelQuestion.id ||
            (ele.clientId && ele.clientId === topLevelQuestion.clientId))
        ) {
          return new ProgramBuilderQuestionInstance({
            ...ele,
            ancillary: {
              ...ele.ancillary,
            },
            title: ele.name,
            children: ele.subQuestions
              ? recursiveTransform(ele.subQuestions)
              : null,
            expanded: true,
          });
        }
        return new ProgramBuilderQuestionInstance({
          ...ele,
          title: ele.name,
          children: ele.subQuestions
            ? recursiveTransform(ele.subQuestions)
            : null,
          expanded: true,
        });
      });
    let form = parentForm;
    if (path) {
      const treeData = form.questionInstances
        ? recursiveTransform(form.questionInstances)
        : [];

      form = new FinalFormModifier({
        ...form,
        // @ts-expect-error
        questionInstances: addNodeUnderParent({
          treeData: treeData,
          parentKey: path[path.length - 1],
          expandParent: true,
          getNodeKey: ({ treeIndex }) => treeIndex,
          newNode: questionInstance,
          addAsFirstChild: false,
        }).treeData,
      });
    } else {
      form = new FinalFormModifier({
        ...form,
        masked: form.masked === 'unknown' ? undefined : form.masked,
        policy: form.policy || undefined,
        questionInstances: form.questionInstances
          ? [...form.questionInstances, questionInstance]
          : [questionInstance], //this seems to be sometimes null?
      });
    }

    dispatch(formModifierStore.actions.updateTreeStructure(form));
  }

  function onCustomSave(
    formState: $TSFixMe,
    onSave: $TSFixMe,
    isEditing: $TSFixMe,
    questionInstance: $TSFixMe,
  ) {
    return onSave(dispatch, formState, isEditing, questionInstance);
  }

  function clearPendingQuestion() {
    dispatch(formModifierStore.actions.setPendingQuestion(null));
  }

  return {
    onQuestionInstanceEdited,
    onQuestionInstanceCreated,
    onCustomSave,
    clearPendingQuestion,
  };
}

export const QuestionInstanceFormConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default QuestionInstanceFormConnect(QuestionInstanceFormWithStyles);
